import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  Button,
  Space,
  Form,
  message,
  Tabs
} from 'antd';
import {
  SafetyCertificateOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useConfig } from '../../contexts/ConfigContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import dayjs from 'dayjs';
import {
  TNumberingRange,
  TCompanyFeConfig,
  TConnectionStatus,
  FeMasterActivationBanner,
  NumberingRangesTab,
  FactusCredentialsTab,
  SyncRangesModal,
  NumberingRangeFormModal
} from './company-electronic-invoicing';

export type { TNumberingRange, TCompanyFeConfig, TConnectionStatus };

interface CompanyElectronicInvoicingModalProps {
  visible: boolean;
  company: any;
  onClose: () => void;
}

export const CompanyElectronicInvoicingModal: React.FC<CompanyElectronicInvoicingModalProps> = ({
  visible,
  company,
  onClose
}) => {
  const { config } = useConfig();
  const { securePost } = useSecureRequest();

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingRange, setSavingRange] = useState(false);
  const [togglingFe, setTogglingFe] = useState(false);

  // Estado Maestro de Facturación Electrónica para la Empresa
  const [isEnabled, setIsEnabled] = useState(false);
  const [feOptionId, setFeOptionId] = useState<number | null>(null);

  // Configuración de la Empresa en Factus (Bolsa propia vs compartida, credenciales)
  const [companyFeConfig, setCompanyFeConfig] = useState<TCompanyFeConfig>({
    is_shared_pool: true,
    client_id: '',
    client_secret: '',
    username: '',
    is_configured: false
  });

  // Lista de Rangos de Numeración registrados
  const [ranges, setRanges] = useState<TNumberingRange[]>([]);

  // Estado de prueba de conexión
  const [connectionStatus, setConnectionStatus] = useState<TConnectionStatus>(null);

  // Modales internos
  const [rangeModalVisible, setRangeModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [editingRange, setEditingRange] = useState<TNumberingRange | null>(null);

  // Formularios
  const [configForm] = Form.useForm();
  const [rangeForm] = Form.useForm();
  const [syncForm] = Form.useForm();

  // Carga de estado general, rangos y credenciales
  const fetchCompanyStatusAndRanges = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      // 1. Obtener estado de habilitación desde module/config/get
      const optionsRes = await securePost('/module/config/get', {
        session: config.session,
        company: company.id
      });
      if (Array.isArray(optionsRes)) {
        const feOption = optionsRes.find(
          (opt: any) =>
            (opt.key === 'FACTUS_ENABLED' || opt.type === 'ELECTRONIC_INVOICING') &&
            opt.company_id === company.id
        );
        if (feOption) {
          setIsEnabled(Boolean(feOption.show));
          setFeOptionId(feOption.id || null);
        } else {
          setIsEnabled(false);
          setFeOptionId(null);
        }
      }

      // 2. Obtener rangos de numeración registrados
      const rangesRes = await securePost('/electronic-invoicing/get-ranges', {
        session: config.session,
        companyId: company.id
      });
      if (rangesRes && Array.isArray(rangesRes.ranges)) {
        setRanges(rangesRes.ranges);
      } else {
        setRanges([]);
      }

      // 3. Obtener credenciales y configuración específica de la empresa
      const configRes = await securePost('/electronic-invoicing/get-company-config', {
        session: config.session,
        companyId: company.id
      });
      if (configRes?.status === 200 && configRes.config) {
        const cfg = configRes.config;
        setCompanyFeConfig({
          is_shared_pool: cfg.is_shared_pool ?? true,
          client_id: cfg.client_id || '',
          client_secret: cfg.client_secret || '',
          username: cfg.username || '',
          is_configured: Boolean(cfg.is_configured)
        });
        configForm.setFieldsValue({
          is_shared_pool: cfg.is_shared_pool ?? true,
          client_id: cfg.client_id || '',
          client_secret: cfg.client_secret || '',
          username: cfg.username || ''
        });
      }
    } catch (err: any) {
      console.error('Error fetching FE status/ranges:', err);
      message.error(err.message || 'Error al cargar información de facturación electrónica');
    } finally {
      setLoading(false);
    }
  }, [company?.id, config.session, configForm, securePost]);

  useEffect(() => {
    if (visible && company?.id) {
      fetchCompanyStatusAndRanges();
      setConnectionStatus(null);
    }
  }, [visible, company?.id, fetchCompanyStatusAndRanges]);

  // Switch maestro para activar/desactivar FE
  const handleToggleEnableFE = async (checked: boolean) => {
    if (!company) return;
    setTogglingFe(true);
    try {
      const payloadOption: any = {
        ...(feOptionId ? { id: feOptionId } : {}),
        company_id: company.id,
        type: 'ELECTRONIC_INVOICING',
        key: 'FACTUS_ENABLED',
        string_value: 'Facturación Electrónica DIAN (Factus V2)',
        show: checked,
        is_global: false,
        scope: 'company'
      };

      const res = await securePost('/module/config/update', {
        session: config.session,
        uidCompany: company.id,
        company: company.id,
        data: [payloadOption]
      });

      if (res && typeof res === 'object' && res.status >= 400) {
        throw new Error(res.message || 'Error al guardar configuración');
      }

      setIsEnabled(checked);
      message.success(`Facturación electrónica ${checked ? 'habilitada' : 'deshabilitada'} para ${company.name}`);
      fetchCompanyStatusAndRanges();
    } catch (err: any) {
      console.error('Error toggling FE:', err);
      message.error(err.message || 'Error al cambiar estado de facturación electrónica');
    } finally {
      setTogglingFe(false);
    }
  };

  // Botón Sincronizar: abre modal si es bolsa compartida o ejecuta si es propia
  const handleClickSync = () => {
    if (companyFeConfig.is_shared_pool) {
      syncForm.resetFields();
      setSyncModalVisible(true);
    } else {
      handleExecuteSync({});
    }
  };

  const handleExecuteSync = async (filters: { resolution_number?: string; prefix?: string }) => {
    if (!company) return;

    const cleanRes = filters.resolution_number?.trim();
    const cleanPrefix = filters.prefix?.trim()?.toUpperCase();

    // En bolsa compartida es obligatorio especificar al menos resolución o prefijo
    if (companyFeConfig.is_shared_pool && !cleanRes && !cleanPrefix) {
      message.warning('Para empresas con bolsa compartida es obligatorio ingresar el prefijo o el número de resolución.');
      return;
    }

    setSyncing(true);
    try {
      const res = await securePost('/electronic-invoicing/sync-ranges', {
        session: config.session,
        companyId: company.id,
        filters: {
          resolution_number: cleanRes || undefined,
          prefix: cleanPrefix || undefined
        }
      });
      if (res?.status === 200) {
        const count = Array.isArray(res.ranges) ? res.ranges.length : 0;
        if (count === 0) {
          message.info(res.message || 'No se encontraron rangos con los filtros indicados en Factus');
        } else {
          message.success(res.message || `Se sincronizaron ${count} rangos exitosamente desde Factus`);
        }
        setSyncModalVisible(false);
        fetchCompanyStatusAndRanges();
      } else {
        message.error(res?.message || 'Error al sincronizar rangos con Factus API');
      }
    } catch (err: any) {
      message.error(err.message || 'Error de conexión con Factus API');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenCreateRange = () => {
    setEditingRange(null);
    rangeForm.resetFields();
    rangeForm.setFieldsValue({
      prefix: 'SETP',
      from_number: 1,
      to_number: 10000,
      current_number: 0,
      is_active: true,
      is_default: ranges.length === 0,
      document_type: 'INVOICE'
    });
    setRangeModalVisible(true);
  };

  const handleOpenEditRange = (record: TNumberingRange) => {
    setEditingRange(record);
    rangeForm.setFieldsValue({
      prefix: record.prefix,
      resolution_number: record.resolution_number,
      resolution_date: record.resolution_date ? dayjs(record.resolution_date) : undefined,
      from_number: record.from_number,
      to_number: record.to_number,
      current_number: record.current_number ?? 0,
      valid_from: record.valid_from ? dayjs(record.valid_from) : undefined,
      valid_to: record.valid_to ? dayjs(record.valid_to) : undefined,
      is_active: record.is_active ?? true,
      is_default: record.is_default ?? false,
      document_type: record.document_type || 'INVOICE'
    });
    setRangeModalVisible(true);
  };

  const handleSaveRange = async (values: any) => {
    if (!company) return;
    setSavingRange(true);
    try {
      const rangePayload: any = {
        ...(editingRange?.id ? { id: editingRange.id } : {}),
        ...(editingRange?.factus_range_id ? { factus_range_id: editingRange.factus_range_id } : {}),
        company_id: company.id,
        prefix: values.prefix?.trim().toUpperCase(),
        resolution_number: values.resolution_number?.trim() || null,
        resolution_date: values.resolution_date ? values.resolution_date.format('YYYY-MM-DD') : null,
        from_number: values.from_number,
        to_number: values.to_number,
        current_number: values.current_number ?? 0,
        valid_from: values.valid_from ? values.valid_from.format('YYYY-MM-DD') : null,
        valid_to: values.valid_to ? values.valid_to.format('YYYY-MM-DD') : null,
        document_type: values.document_type || 'INVOICE',
        is_active: values.is_active ?? true,
        is_default: values.is_default ?? false
      };

      const res = await securePost('/electronic-invoicing/save-range', {
        session: config.session,
        range: rangePayload,
        companyId: company.id
      });

      if (res?.status === 200) {
        message.success(res.message || 'Rango de numeración registrado y vinculado con éxito');
        setRangeModalVisible(false);
        fetchCompanyStatusAndRanges();
      } else {
        message.error(res?.message || 'Error al guardar el rango de numeración');
      }
    } catch (err: any) {
      message.error(err.message || 'Error al guardar el rango');
    } finally {
      setSavingRange(false);
    }
  };

  const handleSetDefault = async (record: TNumberingRange) => {
    if (!company) return;
    try {
      const res = await securePost('/electronic-invoicing/save-range', {
        session: config.session,
        range: { ...record, is_default: true },
        companyId: company.id
      });
      if (res?.status === 200) {
        message.success(`Rango "${record.prefix}" establecido como predeterminado`);
        fetchCompanyStatusAndRanges();
      } else {
        message.error(res?.message || 'Error al actualizar rango predeterminado');
      }
    } catch (err: any) {
      console.error('Error al actualizar rango predeterminado:', err);
      message.error(err?.message || 'Error al actualizar rango predeterminado');
    }
  };

  // Probar conexión con Factus
  const handleTestConnection = async () => {
    try {
      const values = await configForm.validateFields();
      setTestingConnection(true);
      setConnectionStatus(null);

      const res = await securePost('/electronic-invoicing/test-connection', {
        session: config.session,
        credentials: {
          client_id: values.client_id,
          client_secret: values.client_secret,
          username: values.username,
          password: values.password
        }
      });

      if (res?.status === 200 && res?.success) {
        setConnectionStatus({
          tested: true,
          success: true,
          message: res.message || 'Autenticación exitosa con Factus API'
        });
        message.success(res.message || 'Conexión exitosa con Factus API');
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          message: res?.message || 'Error de autenticación con Factus'
        });
        message.error(res?.message || 'Error de autenticación con Factus');
      }
    } catch (err: any) {
      if (err.errorFields) return;
      setConnectionStatus({
        tested: true,
        success: false,
        message: err.message || 'Error al verificar conexión'
      });
      message.error(err.message || 'Error al verificar conexión');
    } finally {
      setTestingConnection(false);
    }
  };

  // Guardar configuración de credenciales
  const handleSaveConfig = async (values: any) => {
    if (!company?.id) return;
    setSavingConfig(true);
    try {
      const res = await securePost('/electronic-invoicing/save-company-config', {
        session: config.session,
        companyId: company.id,
        config: {
          is_shared_pool: values.is_shared_pool ?? true,
          client_id: values.client_id?.trim() || '',
          client_secret: values.client_secret?.trim() || '',
          username: values.username?.trim() || '',
          password: values.password || ''
        }
      });

      if (res?.status === 200) {
        message.success('Credenciales y configuración de Factus guardadas con éxito');
        fetchCompanyStatusAndRanges();
      } else {
        message.error(res?.message || 'Error al guardar configuración');
      }
    } catch (err: any) {
      message.error(err.message || 'Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <Modal
      title={
        <Space align="center">
          <SafetyCertificateOutlined style={{ color: '#52c41a', fontSize: 20 }} />
          <span>Facturación Electrónica DIAN - {company?.name}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={980}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Cerrar
        </Button>
      ]}
      destroyOnHidden
    >
      <FeMasterActivationBanner
        isEnabled={isEnabled}
        isSharedPool={companyFeConfig.is_shared_pool}
        togglingFe={togglingFe}
        onToggleEnableFE={handleToggleEnableFE}
      />

      <Tabs
        defaultActiveKey="ranges"
        items={[
          {
            key: 'ranges',
            label: (
              <span>
                <SafetyCertificateOutlined /> Rangos de Numeración DIAN
              </span>
            ),
            children: (
              <NumberingRangesTab
                ranges={ranges}
                loading={loading}
                syncing={syncing}
                isSharedPool={companyFeConfig.is_shared_pool}
                onClickSync={handleClickSync}
                onOpenCreateRange={handleOpenCreateRange}
                onOpenEditRange={handleOpenEditRange}
                onSetDefault={handleSetDefault}
              />
            )
          },
          {
            key: 'config',
            label: (
              <span>
                <KeyOutlined /> Credenciales y Conexión Factus
              </span>
            ),
            children: (
              <FactusCredentialsTab
                form={configForm}
                connectionStatus={connectionStatus}
                testingConnection={testingConnection}
                savingConfig={savingConfig}
                onTestConnection={handleTestConnection}
                onSaveConfig={handleSaveConfig}
              />
            )
          }
        ]}
      />

      <SyncRangesModal
        visible={syncModalVisible}
        form={syncForm}
        syncing={syncing}
        onClose={() => setSyncModalVisible(false)}
        onExecuteSync={handleExecuteSync}
      />

      <NumberingRangeFormModal
        visible={rangeModalVisible}
        editingRange={editingRange}
        form={rangeForm}
        savingRange={savingRange}
        onClose={() => setRangeModalVisible(false)}
        onSaveRange={handleSaveRange}
      />
    </Modal>
  );
};

export default CompanyElectronicInvoicingModal;
